import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import type IntentSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/intent/IntentSignalFilters";
import type { SignalNameSelectionOverflowOn } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalNameSegments";
import type { SignalSelectionTooltipLabels } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalSelectionTooltipLabels";
import type { TranslatableNameWithType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import type UISignalDataDeps from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignalDataDeps";
import filterService from "app/pages/sales-intelligence/sub-modules/signals/services/filters/filterService";
import type UISignalNameBuilder from "app/pages/sales-intelligence/sub-modules/signals/services/ui-signals/UISignalNameBuilder";
import { boldFilterSelectionTranslatable } from "app/pages/sales-intelligence/sub-modules/signals/utils/boldSignalValue";
import {
    filterSelectionTranslatable,
    isPartialFilterName,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/filterSelectionNaming";

function buildCountriesFilterName<F extends { countries: readonly number[] }>(
    filters: Partial<F>,
    userCountries: UISignalDataDeps["countries"],
): Readonly<TranslatableNameWithType> {
    const nameBuilder = filterService.getFilterName(FilterType.Countries);

    return nameBuilder.buildName(filters, userCountries, {
        i18nKeyModifier: `${FilterType.Countries}-intent`,
    });
}

function getCountryLabels(
    filters: Partial<IntentSignalFilters>,
    userCountries: UISignalDataDeps["countries"],
): readonly string[] {
    const { countries = [] } = filters;

    return userCountries.filter(({ id }) => countries.includes(id)).map(({ text }) => text);
}

function buildTopicsFilterName<F extends { topics: readonly string[] }>(
    filters: Partial<F>,
    userTopics: UISignalDataDeps["intentSignalSelectedTopics"],
): Readonly<TranslatableNameWithType> {
    const nameBuilder = filterService.getFilterName(FilterType.IntentTopics);

    return nameBuilder.buildName(filters, userTopics);
}

export default function createIntentUISignalNameBuilder(
    deps: Pick<UISignalDataDeps, "intentUserCountries" | "intentSignalSelectedTopics">,
): UISignalNameBuilder<IntentSignalFilters> {
    return {
        buildName({ filters, signalType }: Signal<IntentSignalFilters>) {
            const topicsFilterName = buildTopicsFilterName(filters, deps.intentSignalSelectedTopics);

            return {
                key: `si.signals.${signalType}.title.topics-with-countries`,
                replacements: {
                    topics: boldFilterSelectionTranslatable(topicsFilterName),
                    countries: boldFilterSelectionTranslatable(
                        buildCountriesFilterName(filters, deps.intentUserCountries),
                    ),
                },
            };
        },
        buildNameSegments(signal: Signal<IntentSignalFilters>) {
            const topicsFilterName = buildTopicsFilterName(signal.filters, deps.intentSignalSelectedTopics);
            const countriesFilterName = buildCountriesFilterName(signal.filters, deps.intentUserCountries);
            const selectionOverflowOn: SignalNameSelectionOverflowOn[] = [];

            if (isPartialFilterName(topicsFilterName)) {
                selectionOverflowOn.push("signalType");
            }

            if (isPartialFilterName(countriesFilterName)) {
                selectionOverflowOn.push("signalData");
            }

            return {
                signalType: filterSelectionTranslatable(topicsFilterName),
                signalData: {
                    key: `si.signals.segments.data.${signal.signalType}`,
                    replacements: {
                        countries: filterSelectionTranslatable(countriesFilterName),
                    },
                },
                ...(selectionOverflowOn.length > 0
                    ? {
                          selectionOverflowOn:
                              selectionOverflowOn.length === 1 ? selectionOverflowOn[0] : selectionOverflowOn,
                      }
                    : {}),
            };
        },
        buildSelectionTooltipLabels(signal: Signal<IntentSignalFilters>): SignalSelectionTooltipLabels | undefined {
            const hasTopics = (signal.filters.topics?.length ?? 0) > 0;
            const hasCountries = (signal.filters.countries?.length ?? 0) > 0;

            if (!hasTopics && !hasCountries) {
                return undefined;
            }

            const topicsFilterName = hasTopics
                ? buildTopicsFilterName(signal.filters, deps.intentSignalSelectedTopics)
                : undefined;
            const countriesFilterName = hasCountries
                ? buildCountriesFilterName(signal.filters, deps.intentUserCountries)
                : undefined;
            const topicsPartial = topicsFilterName ? isPartialFilterName(topicsFilterName) : false;
            const countriesPartial = countriesFilterName ? isPartialFilterName(countriesFilterName) : false;

            if (!topicsPartial && !countriesPartial) {
                return undefined;
            }

            const topicLabels =
                topicsPartial && (signal.filters.topics?.length ?? 0) > 1 ? (signal.filters.topics ?? []) : undefined;
            const countryLabels = countriesPartial ? getCountryLabels(signal.filters, deps.intentUserCountries) : [];
            const countryTooltipLabels = countriesPartial && countryLabels.length > 1 ? countryLabels : undefined;

            if (!topicLabels && !countryTooltipLabels) {
                return undefined;
            }

            return {
                ...(topicLabels ? { signalType: topicLabels } : {}),
                ...(countryTooltipLabels ? { signalData: countryTooltipLabels } : {}),
            };
        },
    };
}
